import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLearners } from '../../hooks/useLearners';
import { useLearner } from '../../hooks/useLearner';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import { getEvalStory, startPrewrittenStory } from '../../api/library';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import OnboardingCard from '../../components/common/OnboardingCard';
import OnboardingPageCard from '../../components/common/OnboardingPageCard';
import ToolTipLing from '../../components/common/ToolTipLing';
import shared from './OnboardingPages.module.css';
import s from './PlacementBandPage.module.css';

const STEP_COUNT = 5;

const BANDS = [
  {
    value: 'beginner',
    labelKey: 'placement.beginnerLabel',
    descKey: 'placement.beginnerDesc',
  },
  {
    value: 'intermediate',
    labelKey: 'placement.intermediateLabel',
    // TODO: replace with final copy from K
    descKey: 'placement.intermediateDesc',
  },
  {
    value: 'knowledgeable',
    labelKey: 'placement.knowledgeableLabel',
    // TODO: replace with final copy from K
    descKey: 'placement.knowledgeableDesc',
  },
];

export default function PlacementBandPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: activeLearner } = useLearner();
  const { data: learners = [] } = useLearners();

  const learnerId = location.state?.childProfileId || activeLearner?.id;

  // CurriculumFineTunePage's post-finalize update writes an optimistic patch
  // into the ['learners'] cache (no curriculum join) and invalidates so the
  // server payload (with curriculum) reconciles in the background. Pull from
  // the list first to get the fresh curriculum data; fall back to the active
  // learner if the optimistic update hasn't been replaced yet.
  const learner =
    learners.find((l) => l.id === learnerId) || activeLearner;

  const learnerName = learner?.name || t('childProfile.yourChild');
  const characterCount = learner?.curriculum?.character_count || 0;

  const beginnerEnd = Math.floor(characterCount / 3);
  const intermediateStart = beginnerEnd + 1;
  const intermediateEnd = Math.floor((2 * characterCount) / 3);
  const knowledgeableStart = intermediateEnd + 1;
  const knowledgeableEnd = characterCount;

  function rangeFor(value) {
    if (!characterCount) return null;
    if (value === 'beginner') return { start: 1, end: beginnerEnd };
    if (value === 'intermediate') return { start: intermediateStart, end: intermediateEnd };
    return { start: knowledgeableStart, end: knowledgeableEnd };
  }

  const [selectedBand, setSelectedBand] = useState('beginner');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentStep = 5;

  async function handleContinue() {
    if (!learnerId) {
      setError(t('common.somethingWentWrong'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { templateId } = await getEvalStory(learnerId, selectedBand);
      const { storyId } = await startPrewrittenStory(templateId, learnerId);
      // FP-EVAL-CACHE-01: starting the eval story creates an is_eval row, which
      // flips /onboarding/resume from `placement_band` to `resume_eval`. Eagerly
      // write the new resume payload so OnboardingGuard's loop-prevention check
      // passes on the next render — without this, the cached `placement_band`
      // payload causes a brief redirect back here before the background refetch
      // corrects it (Issue 2).
      queryClient.setQueryData(['onboarding', 'resume'], {
        action: 'resume_eval',
        nextRoute: `/story/read/${storyId}?band=${selectedBand}`,
        learner: null,
      });
      navigate(`/story/read/${storyId}`, {
        replace: true,
        state: { placementBand: selectedBand },
      });
    } catch (err) {
      // BP-ONBOARDING-ATOMICITY-02: eval_story_mappings is seeded for all
      // bands × curricula. A 404 is a real data/server error, not a benign
      // "no eval available" state — surface it inline instead of silently
      // routing the user away to /onboarding/welcome.
      setError(err.message || t('common.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }

  // Shared step counter
  const stepCounter = (
    <div className={shared.stepCounter}>
      {Array.from({ length: STEP_COUNT }, (_, i) => {
        const step = i + 1;
        const isActive = step <= currentStep;
        return (
          <div
            key={step}
            className={`${shared.stepDot} ${isActive ? shared.stepDotActive : ''}`}
            aria-label={`Step ${step} of ${STEP_COUNT}`}
          >
            {step}
          </div>
        );
      })}
    </div>
  );

  const bandCards = (
    <div className={shared.optionCards} role="radiogroup">
      {BANDS.map((b) => {
        const range = rangeFor(b.value);
        return (
          <OnboardingCard
            key={b.value}
            title={t(b.labelKey)}
            description={t(b.descKey)}
            selected={selectedBand === b.value}
            onClick={() => setSelectedBand(b.value)}
            meta={
              range ? (
                <span className={s.rangeHint}>
                  {t('placement.rangeHint', { start: range.start, end: range.end })}
                </span>
              ) : null
            }
          />
        );
      })}
    </div>
  );

  return (
    <main
      className={shared.page}
      style={{ '--cloud-color': '#2AB8A8' }}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* Desktop decoration */}
      <div className={`${shared.topBand} ${shared.topBandTeal}`} />
      <CloudBand variant="teal" className={shared.cloudBandTeal} />
      <WanderWordsLogo className={shared.logo} />

      {/* ───────────── MOBILE / TABLET LAYOUT (hidden ≥1200px) ───────────── */}
      <div className={shared.mobileOnly}>
        <div className={shared.content}>
          <div className={shared.contentRow}>
            <div className={`${shared.leftCol} ${shared.leftColTeal}`}>
              {stepCounter}
              <div className={`${shared.headerRow} ${shared.headerRowTypeB}`}>
                <h1 className={shared.pageTitle}>{t('placement.title')}</h1>
                <div className={shared.subtitleLingRow}>
                  <p className={shared.subtitle}>
                    {t('placement.subtitle', { name: learnerName })}
                  </p>
                  <img
                    src="/assets/ling-onboarding.png"
                    alt="Ling the cat"
                    className={shared.mascotImg}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={shared.mobileCreamSection}>
            <div className={shared.rightCol}>
              {error && (
                <div className={shared.errorBanner} role="alert">{error}</div>
              )}
              {bandCards}
              <div className={shared.spacer} />
            </div>

            <div className={shared.stickyBtnArea}>
              <button
                type="button"
                onClick={handleContinue}
                disabled={isLoading}
                className={shared.continueBtn}
              >
                {isLoading ? t('placement.gettingReady') : t('placement.continue')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── DESKTOP CARD (≥1200px only) ───────────── */}
      <OnboardingPageCard
        stepper={stepCounter}
        leftWidth={392}
        leftContent={
          <>
            <div className={shared.headerColumn}>
              <h1 className={shared.pageTitle}>{t('placement.title')}</h1>
              <p className={shared.subtitle}>
                {t('placement.subtitle', { name: learnerName })}
              </p>
            </div>
            <ToolTipLing type="curious">
              {t('placement.tooltipText', { name: learnerName })}
            </ToolTipLing>
          </>
        }
        rightContent={
          <>
            {error && (
              <div className={shared.errorBanner} role="alert">{error}</div>
            )}
            {bandCards}
            <button
              type="button"
              onClick={handleContinue}
              disabled={isLoading}
              className={shared.continueBtn}
            >
              {isLoading ? t('placement.gettingReady') : t('placement.continue')}
            </button>
          </>
        }
      />
    </main>
  );
}
